import {
    Badge,
    Box,
    Button,
    Container,
    Flex,
    Group,
    Loader,
    LoadingOverlay,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { LineChart, RadarChart } from '@mantine/charts';
import { useMemo, useState } from 'react';

import { DatePickerInput } from '@mantine/dates';
import { MantineChart } from '../../common/MantineChart';
import dayjs from 'dayjs';
import { isRequired } from 'mantine-form-rules';
import { useAuthStore } from '../../../store';
import { useForm } from '@mantine/form';
import useGetTimeSeries from '../../../http/queries/timeseries/getTimeSeries';

const CHART_OPTIONS = [
    { label: 'All Sends', value: 'Sends' },
    { label: 'Carrier', value: 'Carrier' },
];

const SCALE_OPTIONS = [
    { label: '1 hour', value: '_1h' },
    { label: '1 day', value: '_1d' },
];

const DataStatsTimelineModal = ({
    resourceId,
    resource,
    title = 'Send Timeline',
    opened,
    close,
}) => {
    const form = useForm({
        initialValues: {
            type: 'Sends',
            scale: '_1h',
            range: [dayjs().toDate(), dayjs().toDate()],
        },

        transformValues: (values) => {
            const { range, ...rest } = values;
            const [start, end] = range;
            return {
                ...rest,
                start: start != null ? dayjs(start).format('MM-DD-YYYY') : null,
                end: end != null ? dayjs(end).format('MM-DD-YYYY') : null,
            };
        },

        validate: {
            type: isRequired('Type required'),
            scale: isRequired('Scale required'),
            range: (values) => {
                const [start, end] = values || [];
                if (!start || !end) {
                    return 'Start/end required';
                }
            },
            /*emailOrUsername: isNotEmpty('Username or email is required'),
                    password: (val) =>
                        val.length <= 6
                            ? 'Password should include at least 6 characters'
                            : null,*/
        },
    });

    const transformedValues = form.getTransformedValues();
    const { isLoading, data: timeseries } = useGetTimeSeries(
        {
            id: resourceId,
            resource: resource,
            ...transformedValues,
        },
        resourceId != null && form.isValid() && opened
    );

    const data = useMemo(() => {
        if (isLoading || !timeseries) {
            return [];
        }
        const metricKeys = [
            'send',
            'send_fail',
            'send_timeout',
            'unique_click',
            'click',
            'conversion',
        ];
        const { buckets, series } = timeseries;

        const minBucket = dayjs(buckets[0]);
        const maxBucket = dayjs(buckets[buckets.length - 1]);
        const dayRange = maxBucket.diff(minBucket, 'day');

        const labelFormat = dayRange > 1 ? 'MMM D h:mm:ss a' : 'h:mm:ss a';

        return buckets.map((bucket) => {
            const metrics = series[bucket] ?? {};
            const row = { bucket: dayjs(bucket).format(labelFormat) };

            metricKeys.forEach((metric) => {
                row[metric] = metrics[metric] ?? 0; // <- default to 0
            });
            return row;
        });
    }, [timeseries]);

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'xl'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>{title || 'Send Timeline'}</Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <Group>
                        <DatePickerInput
                            type='range'
                            size={'xs'}
                            label={'Time Range'}
                            placeholder='Date range'
                            valueFormat={'MM/DD/YYYY'}
                            allowSingleDateInRange={true}
                            key={form.key('range')}
                            {...form.getInputProps('range')}
                        />
                        <Select
                            size={'xs'}
                            label={'Time Series'}
                            data={SCALE_OPTIONS}
                            defaultValue={'_15m'}
                            key={form.key('scale')}
                            {...form.getInputProps('scale')}
                        />
                    </Group>
                    <Flex pos={'relative'}>
                        <LoadingOverlay
                            visible={isLoading}
                            zIndex={1000}
                            overlayProps={{ radius: 'sm', blur: 2 }}
                        />
                        <MantineChart
                            h={320}
                            type='line'
                            data={data}
                            options={{
                                plugins: {
                                    legend: {
                                        position: 'top',
                                    },
                                    tooltips: {
                                        mode: 'index',
                                    },
                                    zoom: {
                                        zoom: {
                                            wheel: {
                                                enabled: true,
                                            },
                                            drag: {
                                                enabled: true,
                                            },
                                            pinch: {
                                                enabled: true,
                                            },
                                            mode: 'x',
                                            speed: 100,
                                        },
                                        pan: {
                                            enabled: true,
                                            mode: 'x',
                                            speed: 100,
                                        },
                                    },
                                },
                            }}
                            dataKey='bucket'
                            series={[
                                {
                                    name: 'send',
                                    label: 'Sent',
                                    color: 'blue.6',
                                    type: 'line',
                                },

                                {
                                    name: 'unique_click',
                                    label: 'Unique clicks',
                                    color: 'teal.6',
                                    type: 'line',
                                },
                                {
                                    name: 'click',
                                    label: 'Duplicate clicks',
                                    color: 'teal.8',
                                    type: 'line',
                                },
                                {
                                    name: 'conversion',
                                    label: 'Conversions',
                                    color: 'green.4',
                                    type: 'line',
                                },
                            ]}
                            withLegend
                            legendPosition='top'
                            gridAxis='y'
                            tickLine='xy'
                            xAxisLabel='Time'
                            yAxisLabel='Stats'
                            valueFormatter={(v) => v.toLocaleString()}
                        />
                    </Flex>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default DataStatsTimelineModal;
